import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Send, Plus, X, Calendar, MapPin, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';

export default function DemoEventPlanner({ packageData }) {
  const [events, setEvents] = useState([]);
  const [sessionToken, setSessionToken] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Form states
  const [eventName, setEventName] = useState('');
  const [eventDate, setEventDate] = useState('');
  const [eventLocation, setEventLocation] = useState('');
  const [messageText, setMessageText] = useState('');
  const [selectedEventId, setSelectedEventId] = useState(null);

  // Load session
  useEffect(() => {
    const token = localStorage.getItem('demoSessionToken');
    setSessionToken(token);

    if (token) {
      fetchEvents(token);
    }
  }, []);

  const fetchEvents = async (token) => {
    try {
      const res = await fetch(`/api/demo/events/${token}`);
      const data = await res.json();
      
      if (data.status === 'success') {
        setEvents(data.events || []);
      }
    } catch (err) {
      console.error('Failed to fetch events:', err);
    }
  };

  const handleCreateEvent = async () => {
    if (!eventName.trim() || !eventDate) {
      toast.error('Event-Name und Datum erforderlich');
      return;
    }

    if (!sessionToken) {
      toast.error('Session ungültig');
      return;
    }

    // Check event limit
    if (packageData.limits.maxEvents !== null && events.length >= packageData.limits.maxEvents) {
      toast.error(`Max. ${packageData.limits.maxEvents} Events erreicht`);
      return;
    }

    setLoading(true);
    try {
      const res = await fetch('/api/demo/events', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionToken,
          name: eventName,
          date: eventDate,
          location: eventLocation,
        }),
      });

      if (!res.ok) throw new Error('Failed to create event');

      const data = await res.json();
      const newEvent = { ...data.event, messages: [] };
      setEvents([...events, newEvent]);
      
      setEventName('');
      setEventDate('');
      setEventLocation('');
      setSelectedEventId(newEvent.id);
      
      toast.success('🎉 Event erstellt!');
    } catch (err) {
      console.error('Error:', err);
      toast.error('Event konnte nicht erstellt werden');
    } finally {
      setLoading(false);
    }
  };

  const handleAddMessage = async () => {
    if (!messageText.trim() || !selectedEventId) {
      toast.error('Event und Nachricht erforderlich');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch('/api/demo/messages', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionToken,
          text: messageText,
          author: 'Demo User',
        }),
      });

      if (!res.ok) throw new Error('Failed to add message');

      const data = await res.json();
      
      // Update local state
      setEvents(events.map(evt => {
        if (evt.id === selectedEventId) {
          return { ...evt, messages: [...(evt.messages || []), data.message] };
        }
        return evt;
      }));

      setMessageText('');
      toast.success('💬 Nachricht hinzugefügt!');
    } catch (err) {
      console.error('Error:', err);
      toast.error('Nachricht konnte nicht hinzugefügt werden');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteEvent = (id) => {
    setEvents(events.filter(evt => evt.id !== id));
    if (selectedEventId === id) setSelectedEventId(null);
    toast.success('Event gelöscht');
  };

  const selectedEvent = events.find(e => e.id === selectedEventId);
  const maxMessages = packageData.limits.maxMessages;
  const currentMessages = selectedEvent?.messages?.length || 0;

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg p-6 mb-6"
      >
        <h2 className="text-2xl font-bold mb-2">{packageData.icon} {packageData.name} Event-Planer</h2>
        <p className="text-blue-100">
          Erstelle Events und füge Nachrichten von Freunden hinzu
        </p>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Left: Event Creation & List */}
        <div className="space-y-4">
          <div className="bg-white rounded-lg p-6 shadow-lg">
            <h3 className="font-bold text-lg mb-4">📅 Neues Event</h3>
            
            <input
              type="text"
              value={eventName}
              onChange={(e) => setEventName(e.target.value)}
              placeholder="Event-Name"
              className="w-full border border-gray-300 rounded-lg p-2 mb-3 focus:outline-none focus:ring-2 focus:ring-blue-400"
            />

            <input
              type="date"
              value={eventDate}
              onChange={(e) => setEventDate(e.target.value)}
              className="w-full border border-gray-300 rounded-lg p-2 mb-3 focus:outline-none focus:ring-2 focus:ring-blue-400"
            />

            <input
              type="text"
              value={eventLocation}
              onChange={(e) => setEventLocation(e.target.value)}
              placeholder="Ort (optional)"
              className="w-full border border-gray-300 rounded-lg p-2 mb-4 focus:outline-none focus:ring-2 focus:ring-blue-400"
            />

            <button
              onClick={handleCreateEvent}
              disabled={loading || !eventName.trim() || !eventDate}
              className="w-full bg-blue-500 text-white py-2 rounded-lg hover:bg-blue-600 disabled:opacity-50 transition flex items-center justify-center gap-2"
            >
              <Plus size={18} /> Event erstellen
            </button>

            {/* Limit Info */}
            <div className="mt-4 p-3 bg-gray-50 rounded-lg text-sm">
              <p className="text-gray-700">
                Events: <strong>{events.length}/{maxMessages === null ? '∞' : maxMessages}</strong>
              </p>
            </div>
          </div>

          {/* Event List */}
          <div className="bg-white rounded-lg p-6 shadow-lg">
            <h3 className="font-bold text-lg mb-3">Events ({events.length})</h3>
            {events.length === 0 ? (
              <p className="text-gray-500 text-center py-4">Keine Events</p>
            ) : (
              <div className="space-y-2">
                {events.map((evt) => (
                  <motion.button
                    key={evt.id}
                    whileHover={{ scale: 1.02 }}
                    onClick={() => setSelectedEventId(evt.id)}
                    className={`w-full text-left p-3 rounded-lg border transition ${
                      selectedEventId === evt.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300 bg-gray-50'
                    }`}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <p className="font-bold">{evt.name}</p>
                        <p className="text-xs text-gray-600 flex items-center gap-1 mt-1">
                          <Calendar size={14} /> {new Date(evt.date).toLocaleDateString('de-DE')}
                        </p>
                        {evt.location && (
                          <p className="text-xs text-gray-600 flex items-center gap-1">
                            <MapPin size={14} /> {evt.location}
                          </p>
                        )}
                        <p className="text-xs text-gray-600 mt-1">
                          💬 {evt.messages?.length || 0} Nachrichten
                        </p>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteEvent(evt.id);
                        }}
                        className="text-red-500 hover:text-red-700 p-1"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </motion.button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right: Message Addition */}
        <div className="bg-white rounded-lg p-6 shadow-lg">
          <h3 className="font-bold text-lg mb-4">
            💬 {selectedEvent ? `Nachrichten für "${selectedEvent.name}"` : 'Wähle ein Event'}
          </h3>

          {selectedEvent ? (
            <>
              {/* Message List */}
              <div className="mb-6 max-h-64 overflow-y-auto space-y-2 mb-6">
                {selectedEvent.messages?.length === 0 ? (
                  <p className="text-gray-500 text-center py-6 bg-gray-50 rounded-lg">
                    Noch keine Nachrichten 👋
                  </p>
                ) : (
                  selectedEvent.messages?.map((msg, idx) => (
                    <motion.div
                      key={msg.id}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.05 }}
                      className="bg-blue-50 p-3 rounded-lg border-l-4 border-blue-400"
                    >
                      <p className="text-gray-700 text-sm">{msg.text}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        {new Date(msg.createdAt).toLocaleTimeString('de-DE')}
                      </p>
                    </motion.div>
                  ))
                )}
              </div>

              {/* Message Limit */}
              <div className="mb-4 p-3 bg-yellow-50 rounded-lg text-sm border-l-4 border-yellow-400">
                <p className="text-yellow-900">
                  <strong>
                    Nachrichten: {currentMessages}/{maxMessages === null ? '∞' : maxMessages}
                  </strong>
                </p>
              </div>

              {/* Add Message */}
              {maxMessages === null || currentMessages < maxMessages ? (
                <div className="space-y-2">
                  <textarea
                    value={messageText}
                    onChange={(e) => setMessageText(e.target.value)}
                    placeholder="Schreib eine Nachricht..."
                    maxLength={300}
                    rows={3}
                    className="w-full border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none"
                  />
                  <span className="text-xs text-gray-500">{messageText.length}/300</span>
                  <button
                    onClick={handleAddMessage}
                    disabled={loading || !messageText.trim()}
                    className="w-full bg-blue-500 text-white py-2 rounded-lg hover:bg-blue-600 disabled:opacity-50 transition flex items-center justify-center gap-2"
                  >
                    <Send size={18} /> {loading ? 'Speichern...' : 'Nachricht speichern'}
                  </button>
                </div>
              ) : (
                <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm border-l-4 border-red-400">
                  <p><strong>Nachricht-Limit erreicht!</strong></p>
                  <p className="text-xs mt-1">Upgrade auf ein höheres Paket für mehr Nachrichten.</p>
                </div>
              )}
            </>
          ) : (
            <p className="text-center py-12 text-gray-500">Wähle ein Event aus, um Nachrichten hinzuzufügen</p>
          )}
        </div>
      </div>
    </div>
  );
}
